
import { SleepRecord } from "@/types";

/**
 * Calculates sleep latency (time taken to fall asleep) in minutes
 */
export const calculateLatency = (record: SleepRecord | undefined): number | 'N/A' => {
  if (!record) return 'N/A';
  
  try {
    const startTime = new Date(record.start_time);
    const endTime = new Date(record.end_time);
    
    // Check if dates are valid
    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
      console.error("Invalid date format in calculateLatency:", { startTime: record.start_time, endTime: record.end_time });
      return 'N/A';
    }
    
    return Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
  } catch (error) {
    console.error("Error in calculateLatency:", error);
    return 'N/A';
  }
};

/**
 * Calculates average sleep latency across multiple records
 */
export const calculateAverageLatency = (records: SleepRecord[]): number | 'N/A' => {
  if (!records.length) return 'N/A';
  
  try {
    const validDurations = records
      .map(record => {
        try {
          const startTime = new Date(record.start_time);
          const endTime = new Date(record.end_time);
          
          // Check if dates are valid
          if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
            console.error("Invalid date format in calculateAverageLatency:", { startTime: record.start_time, endTime: record.end_time });
            return null;
          }
          
          return Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
        } catch (error) {
          console.error("Error processing record in calculateAverageLatency:", error);
          return null;
        }
      })
      .filter((duration): duration is number => duration !== null);
    
    if (validDurations.length === 0) return 'N/A';
    
    return Math.round(
      validDurations.reduce((acc, curr) => acc + curr, 0) / validDurations.length
    );
  } catch (error) {
    console.error("Error in calculateAverageLatency:", error);
    return 'N/A';
  }
};

/**
 * Calculates consecutive days with consistent sleep schedule
 */
export const calculateSleepStreak = (records: SleepRecord[]) => {
  if (!records.length) return 0;
  let streak = 1;
  const timeWindow = 2 * 60 * 60 * 1000; // 2 hour window for consistency
  
  for (let i = 1; i < records.length; i++) {
    const prevBedtime = new Date(records[i-1].start_time);
    const currBedtime = new Date(records[i].start_time);
    const diffDays = Math.floor((prevBedtime.getTime() - currBedtime.getTime()) / (1000 * 60 * 60 * 24));
    const timeDeviation = Math.abs(prevBedtime.getHours() - currBedtime.getHours()) * 60 * 60 * 1000 +
                         Math.abs(prevBedtime.getMinutes() - currBedtime.getMinutes()) * 60 * 1000;
    
    // Count as streak if within 24±2 hours and consistent bedtime window
    if (diffDays === 1 && timeDeviation <= timeWindow) streak++;
    else break;
  }
  return streak;
};

/**
 * Counts the number of restful sleep sessions
 */
export const checkRestfulness = (records: SleepRecord[]) => {
  if (!records.length) return 0;
  return records.filter(r => r.restfulness?.rating === "good").length;
};

/**
 * Checks if bedtime has been consistent over the last week
 */
export const checkBedtimeConsistency = (records: SleepRecord[]) => {
  if (records.length < 7) return false;
  const bedtimes = records.slice(0, 7).map(r => {
    const date = new Date(r.start_time);
    return date.getHours() * 60 + date.getMinutes(); // Convert to minutes
  });
  const timeWindow = 30; // 30 minutes window for consistency
  return bedtimes.every(time => Math.abs(time - bedtimes[0]) <= timeWindow);
};

/**
 * Analyzes how easily the child falls asleep
 */
export const calculateSleepOnset = (record: SleepRecord) => {
  return record.restfulness?.rating === "good" ? true : false;
};

/**
 * Checks if child is showing signs of self-soothing
 */
export const checkSelfSoothing = (records: SleepRecord[]) => {
  const goodSleepCount = checkRestfulness(records);
  return goodSleepCount >= 5;
};

/**
 * Analyzes sleep data and returns achievements status
 */
export const analyzeSleepData = (records: SleepRecord[]) => {
  if (!records.length) {
    return {
      sleepStreak: 0,
      restfulNights: 0,
      isConsistentBedtime: false,
      isSmoothSleepOnset: false,
      isSelfSoothing: false
    };
  }

  return {
    sleepStreak: calculateSleepStreak(records),
    restfulNights: checkRestfulness(records),
    isConsistentBedtime: checkBedtimeConsistency(records),
    isSmoothSleepOnset: calculateSleepOnset(records[0]),
    isSelfSoothing: checkSelfSoothing(records)
  };
};
